<template>
    <Modal
        :show="show"
        size="small"
        :featureAnnouncementLayout="true"
        :closeOnBackdropClick="false"
        @onModalClose="handleClose"
    >
        <template #media>
            <div class="FeatureAnnouncementModal-media">
                <div v-if="showPlatformSelector" class="PlatformSelector">
                    <button
                        class="PlatformButton"
                        :class="{ 'is-active': selectedPlatform === 'ios' }"
                        @click="selectedPlatform = 'ios'"
                    >
                        <i class="ri-apple-line"></i>
                        <span>iOS</span>
                    </button>
                    <button
                        class="PlatformButton"
                        :class="{ 'is-active': selectedPlatform === 'android' }"
                        @click="selectedPlatform = 'android'"
                    >
                        <i class="ri-android-line"></i>
                        <span>Android</span>
                    </button>
                </div>
                <VideoPlayer
                    v-if="videoSrc"
                    :key="videoSrc"
                    :src="videoSrc"
                    :poster="announcement.videoPoster"
                    :title="announcement?.title"
                    :autoplay="true"
                    :loop="true"
                    class="FeatureAnnouncementModal-video"
                />
            </div>
        </template>
        <template #header>
            <span class="FeatureAnnouncementModal-badge">
                <i class="ri-sparkling-2-fill"></i>
                <span>Neu</span>
            </span>
            <h3 class="FeatureAnnouncementModal-title">{{ announcement?.title }}</h3>
        </template>
        <div class="FeatureAnnouncementModal-content">
            <div v-html="announcement?.content"></div>
        </div>

        <template #actions>
            <Button variant="plain" @onClick="handleDismiss" v-if="showDontShowAgain">
                Später ansehen
            </Button>
            <Button variant="success" @onClick="handleDontShowAgain"> Super, danke! </Button>
        </template>
    </Modal>
</template>

<script>
import Modal from '@/components/widgets/Modal.vue';
import Button from '@/components/widgets/Button.vue';
import VideoPlayer from '@/components/VideoPlayer.vue';
import pwaService from '@/services/pwaService';

export default {
    name: 'FeatureAnnouncementModal',
    components: {
        Modal,
        Button,
        VideoPlayer,
    },
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        announcement: {
            type: Object,
            default: null,
        },
        showDontShowAgain: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            selectedPlatform: null,
        };
    },
    computed: {
        isDesktop() {
            return typeof window !== 'undefined' && window.innerWidth >= 1024;
        },
        hasMultipleVideos() {
            if (!this.announcement?.video) return false;
            const video = this.announcement.video;
            return typeof video === 'object' && video.ios && video.android;
        },
        showPlatformSelector() {
            return this.isDesktop && this.hasMultipleVideos;
        },
        videoSrc() {
            if (!this.announcement?.video) return null;

            const video = this.announcement.video;

            if (typeof video === 'string') {
                return video;
            }

            if (typeof video === 'object') {
                if (this.showPlatformSelector && this.selectedPlatform) {
                    return video[this.selectedPlatform] || video.default || null;
                }

                if (pwaService.isIOS() && video.ios) {
                    return video.ios;
                }
                if (pwaService.isAndroid() && video.android) {
                    return video.android;
                }
                return video.default || video.ios || video.android || null;
            }

            return null;
        },
    },
    watch: {
        announcement: {
            handler(newAnnouncement) {
                if (newAnnouncement?.video && typeof newAnnouncement.video === 'object') {
                    if (this.showPlatformSelector && !this.selectedPlatform) {
                        this.selectedPlatform = 'ios';
                    }
                } else {
                    this.selectedPlatform = null;
                }
            },
            immediate: true,
        },
        showPlatformSelector(newVal) {
            if (newVal && !this.selectedPlatform) {
                this.selectedPlatform = 'ios';
            } else if (!newVal) {
                this.selectedPlatform = null;
            }
        },
    },
    mounted() {
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleResize() {
            this.$forceUpdate();
        },
        handleClose() {
            this.$emit('close');
        },
        handleDismiss() {
            this.$emit('dismiss', this.announcement?.id);
        },
        handleDontShowAgain() {
            this.$emit('dont-show-again', this.announcement?.id);
        },
    },
};
</script>

<style lang="scss">
.FeatureAnnouncementModal-content {
    font-size: 14px;
    line-height: 1.6;
    color: var(--color-text-primary, #333);

    p {
        margin: 0 0 16px 0;
        line-height: 1.6;

        &:last-child {
            margin-bottom: 0;
        }
    }

    img {
        width: 100%;
        height: auto;
        border-radius: 8px;
        display: block;
    }

    ul,
    ol {
        margin: 16px 0;
        padding-left: 24px;
        line-height: 1.8;

        &:last-child {
            margin-bottom: 0;
        }
    }

    ul {
        list-style-type: disc;
    }

    ol {
        list-style-type: decimal;
    }

    li {
        margin-bottom: 8px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        margin: 24px 0 16px 0;
        font-weight: 600;
        line-height: 1.4;

        &:first-child {
            margin-top: 0;
        }
    }

    h1 {
        font-size: 24px;
    }

    h2 {
        font-size: 20px;
    }

    h3 {
        font-size: 18px;
    }

    h4 {
        font-size: 16px;
    }

    a {
        color: var(--color-blue, #0066cc);
        text-decoration: none;
        transition: color 0.2s ease;

        &:hover {
            color: var(--color-blue-dark, #0052a3);
            text-decoration: underline;
        }
    }

    strong,
    b {
        font-weight: 600;
    }

    em {
        font-style: italic;
    }

    i {
        font-style: italic;

        &[class^='ri-'] {
            font-style: normal;
        }
    }

    code {
        background-color: #f5f5f5;
        padding: 2px 6px;
        border-radius: 4px;
        font-family: 'Courier New', monospace;
        font-size: 0.9em;
    }

    blockquote {
        margin: 16px 0;
        padding: 12px 16px;
        border-left: 4px solid var(--color-border, #ddd);
        background-color: #f9f9f9;
        font-style: italic;
    }
}

.FeatureAnnouncementModal-media {
    position: relative;
    width: 100%;
}

.FeatureAnnouncementModal-video {
    width: 100%;
}

.PlatformSelector {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 10;
    display: flex;
    gap: 4px;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(8px);
    border-radius: 6px;
    padding: 4px;
}

.PlatformButton {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
    border-radius: 4px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    i {
        font-size: 1rem;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: rgba(255, 255, 255, 0.9);
    }

    &.is-active {
        background: rgba(255, 255, 255, 0.2);
        color: white;
    }
}

.FeatureAnnouncementModal-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
    flex-shrink: 0;

    i {
        font-size: 0.875rem;
        font-style: normal;
    }
}

.FeatureAnnouncementModal-title {
    margin: 0;
}
</style>
